import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Edit2, ExternalLink, Loader2, Star, Zap,
  Mail, User, Briefcase, Building2, BarChart3,
  Hash, Activity, TrendingUp, ArrowRight,
} from 'lucide-react';
import type { Candidate, RoleFitProjection } from '@core/types';
import { candidateService } from '@core/supabase/services/candidateService';
import { CandidateForm } from '@modules/candidates/CandidateForm';
import { CandidateCollapsiblePanel } from '@modules/candidates/CandidateCollapsiblePanel';
import { FitmentStarRating } from './FitmentStarRating';
import { GapList } from './GapList';
import {
  OptimizedResumePreviewModal,
  type OptimizedResumePreviewModalPayload,
} from './OptimizedResumePreviewModal';
import { loadResumePreviewPayloadFromStoragePath } from './fetchResumePreviewPayload';
import {
  parseSavedProfileResumeFile,
  type SavedProfileDesign,
} from '@modules/single-optimize/aiResumeOptimizer/utils/savedProfileResumeFile';
import type { RoleFitRow } from './computePairFit';
import { useAuth } from '@core/auth/useAuth';
import { formatDateTimeGmt } from '@shared/lib/formatDateTimeGmt';

export type RoleFitPairTabId = 'overview' | 'emails' | 'fitment' | 'parameters' | 'projection-analysis';

const BRAND = '#8B80F9';

const SCORE_BANDS = [
  { label: 'Excellent', min: 75, color: '#22c55e' },
  { label: 'Good',      min: 55, color: '#3b82f6' },
  { label: 'Fair',      min: 35, color: '#f59e0b' },
  { label: 'Poor',      min: 0,  color: '#ef4444' },
];

function scoreBand(s: number) {
  return SCORE_BANDS.find((b) => s >= b.min) ?? SCORE_BANDS[SCORE_BANDS.length - 1]!;
}

function RecruiterStarsReadOnly({ rating }: { rating: number | null | undefined }) {
  const r = rating ?? 0;
  return (
    <div className="flex items-center gap-0.5" aria-label={r ? `Rating ${r} of 5` : 'No rating'}>
      {[1, 2, 3, 4, 5].map((n) => (
        <Star key={n} className={`h-4 w-4 ${n <= r ? 'fill-amber-400 text-amber-400' : 'vscode-text-tertiary'}`} aria-hidden />
      ))}
      {!r ? <span className="ml-2 text-xs vscode-text-tertiary">Not set</span> : null}
    </div>
  );
}

function skillsFromAnalysisJson(v: unknown): string[] {
  if (!Array.isArray(v)) return [];
  return v
    .map((x) => {
      if (typeof x === 'string') return x;
      if (x && typeof x === 'object' && 'skill' in x) return String((x as { skill: unknown }).skill);
      return '';
    })
    .filter(Boolean);
}

/* ─── Shared attr row helper ─────────────────────────────────── */
const DT_CLS = 'vscode-text-secondary pr-2 min-w-0';
const DD_CLS = 'vscode-text-primary min-w-0 break-words';

export function RoleFitPairDetailView({
  row,
  orgId,
  userId,
  activeTab,
  setTab,
  onCandidateSaved,
  optimizeReturnTo = '/role-fit',
  savedProjection,
  projectionLoading = false,
}: {
  row: RoleFitRow;
  orgId: string;
  userId: string;
  activeTab: RoleFitPairTabId;
  setTab: (t: RoleFitPairTabId) => void;
  onCandidateSaved: (c: Candidate) => void;
  optimizeReturnTo?: string;
  savedProjection?: RoleFitProjection | null;
  projectionLoading?: boolean;
}) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [formKey, setFormKey] = useState(0);

  const r = row;
  const clientHref = `/clients/${r.job.client_id}`;
  const band = scoreBand(r.fitmentScore);

  const optimizedResumeModalHeadline = [r.job.title?.trim(), r.job.client?.name?.trim() || r.candidate.full_name?.trim()]
    .filter(Boolean)
    .join(' — ');
  const candidateProfileResumeHeadline = [r.candidate.current_title?.trim(), r.candidate.full_name?.trim()]
    .filter(Boolean)
    .join(' — ');

  type ResumePreviewModalState = {
    payload: OptimizedResumePreviewModalPayload;
    headline: string;
    eyebrow?: string;
  } | null;

  const [resumePreviewModal, setResumePreviewModal] = useState<ResumePreviewModalState>(null);
  const [profilePreviewBusy, setProfilePreviewBusy] = useState(false);

  const openOverviewBaseResumePreview = async () => {
    const path = r.candidate.base_resume_url;
    if (!path) return;
    setProfilePreviewBusy(true);
    try {
      const result = await loadResumePreviewPayloadFromStoragePath(path);
      if (result.mode === 'external') {
        window.open(result.href, '_blank', 'noopener,noreferrer');
        return;
      }
      setResumePreviewModal({
        payload: result.payload,
        headline: candidateProfileResumeHeadline || r.candidate.full_name || 'Candidate resume',
        eyebrow: 'Optimized resume (saved to profile)',
      });
    } catch {
      /* ignore */
    } finally {
      setProfilePreviewBusy(false);
    }
  };

  useEffect(() => {
    setEditing(false);
    setFormKey((k) => k + 1);
  }, [r.key]);

  const runOptimize = () => {
    navigate('/single-optimize', {
      state: {
        fromRoleFit: {
          candidateName:  r.candidate.full_name,
          candidateEmail: r.candidate.email ?? undefined,
          jobTitle:       r.job.title,
          clientName:     r.job.client?.name ?? undefined,
          candidateId:    r.candidate.id,
          jobId:          r.job.id,
          returnTo:       optimizeReturnTo,
        },
      },
    });
  };

  const [resumeOpenHref, setResumeOpenHref] = useState<string | null>(null);

  useEffect(() => {
    const path = r.candidate.base_resume_url;
    if (!path) { setResumeOpenHref(null); return; }
    let cancelled = false;
    candidateService
      .getResumeSignedUrl(path)
      .then((u) => { if (!cancelled) setResumeOpenHref(u); })
      .catch(() => { if (!cancelled) setResumeOpenHref(candidateService.getResumeUrl(path)); });
    return () => { cancelled = true; };
  }, [r.candidate.base_resume_url]);

  type SavedResumePreview =
    | { status: 'idle' }
    | { status: 'loading' }
    | { status: 'text'; body: string; openHref: string; templateId: string; design?: SavedProfileDesign }
    | { status: 'pdf'; openHref: string }
    | { status: 'external'; href: string; label: string };

  const [savedResumePreview, setSavedResumePreview] = useState<SavedResumePreview>({ status: 'idle' });

  useEffect(() => {
    if (activeTab !== 'fitment') return;
    const path = r.candidate.base_resume_url;
    if (!path) { setSavedResumePreview({ status: 'idle' }); return; }
    let cancelled = false;
    setSavedResumePreview({ status: 'loading' });
    (async () => {
      let href: string;
      try { href = await candidateService.getResumeSignedUrl(path); }
      catch { href = candidateService.getResumeUrl(path); }
      if (cancelled) return;
      const isPdf = /\.pdf$/i.test(path);
      const isPlainText = /\.txt$/i.test(path);
      if (isPdf) { setSavedResumePreview({ status: 'pdf', openHref: href }); return; }
      if (!isPlainText) { setSavedResumePreview({ status: 'external', href, label: 'Open saved resume file' }); return; }
      fetch(href)
        .then((res) => {
          if (!res.ok) throw new Error(String(res.status));
          return res.text();
        })
        .then((t) => {
          if (cancelled) return;
          const rawFile = t.replace(/^\uFEFF/, '').trim();
          const parsed = parseSavedProfileResumeFile(rawFile);
          setSavedResumePreview({ status: 'text', body: parsed.body || '(Empty file)', templateId: parsed.templateId, design: parsed.design, openHref: href });
        })
        .catch(() => {
          if (!cancelled) setSavedResumePreview({ status: 'external', href, label: 'Open saved resume file (inline preview unavailable)' });
        });
    })();
    return () => { cancelled = true; };
  }, [activeTab, r.candidate.base_resume_url, r.candidate.id]);

  useEffect(() => {
    if (!resumePreviewModal) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setResumePreviewModal(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [resumePreviewModal]);

  /* ─── Tab definitions ────────────────────────────────────────── */
  const TABS: { id: RoleFitPairTabId; label: string }[] = [
    { id: 'overview',             label: 'Overview'           },
    { id: 'emails',               label: 'Emails'             },
    { id: 'fitment',              label: 'Fitment'            },
    { id: 'projection-analysis',  label: 'Projection Analysis'},
    { id: 'parameters',           label: 'Parameters'         },
  ];

  return (
    <div className="space-y-0">

      {/* ── Tabs ── */}
      <nav className="ggj-cand-tablist mb-0" aria-label="Pair detail sections">
        {TABS.map((t) => (
          <button key={t.id} type="button"
            className={`ggj-cand-tab${activeTab === t.id ? ' ggj-cand-tab--active' : ''}`}
            onClick={() => { setTab(t.id); if (t.id !== 'overview') setEditing(false); }}>
            {t.label}
          </button>
        ))}
      </nav>

      {/* ── Toolbar ── */}
      <div className="ggj-profile-toolbar mb-5">
        {activeTab === 'overview' && (
          editing ? (
            <>
              <button type="submit" form="role-fit-candidate-form" className="ggj-toolbar-btn">
                Save
              </button>
              <button type="button" className="ggj-toolbar-btn"
                onClick={() => { setEditing(false); setFormKey((k) => k + 1); }}>
                Discard
              </button>
            </>
          ) : (
            <>
              <button type="button" className="ggj-toolbar-btn inline-flex items-center gap-1.5"
                onClick={() => setEditing(true)}>
                <Edit2 className="w-3.5 h-3.5" /> Edit Candidate
              </button>
              <Link to="/emails/compose" className="ggj-toolbar-btn inline-flex items-center gap-1.5">
                <Mail className="w-3.5 h-3.5" /> Compose Email
              </Link>
            </>
          )
        )}
        {activeTab === 'fitment' && (
          <button type="button" className="ggj-toolbar-btn inline-flex items-center gap-1.5"
            onClick={runOptimize}>
            <Zap className="w-3.5 h-3.5" /> Run Optimize
          </button>
        )}
        {activeTab === 'projection-analysis' && !savedProjection && (
          <Link to="/role-fit-ai/1-1" className="ggj-toolbar-btn inline-flex items-center gap-1.5">
            <BarChart3 className="w-3.5 h-3.5" /> Open RoleFit AI
          </Link>
        )}
        {activeTab === 'parameters' && (
          <button type="button" className="ggj-toolbar-btn inline-flex items-center gap-1.5"
            onClick={() => { setTab('overview'); setEditing(true); }}>
            <Edit2 className="w-3.5 h-3.5" /> Edit candidate
          </button>
        )}
        <Link to={`/candidates/${r.candidate.id}`} className="ggj-toolbar-btn inline-flex items-center gap-1.5 ml-auto">
          <ExternalLink className="w-3.5 h-3.5" /> Full Candidate Page
        </Link>
      </div>

      {/* ── Two-column layout; Parameters tab is full width (no right rail) ── */}
      <div className={`grid grid-cols-1 gap-6 items-start ${activeTab === 'parameters' ? '' : 'lg:grid-cols-[1fr_340px]'}`}>

        {/* ── LEFT: tab content ── */}
        <div className={`min-w-0 space-y-4 ${activeTab === 'parameters' ? 'gg-app-parameters' : ''}`}>

          {/* ════ OVERVIEW TAB ════ */}
          {activeTab === 'overview' && editing && (
            <div className="ggj-param-group overflow-hidden">
              <div className="ggj-profile-section-h flex items-center gap-2">
                <User className="w-3.5 h-3.5" style={{ color: BRAND }} /> Edit Candidate
              </div>
              <div className="ggj-profile-section-body space-y-4">
                <p className="text-xs vscode-text-tertiary">
                  Edit updates the candidate record (resume, rating, profile). Fit scores refresh when you save.
                </p>
                <CandidateForm
                  key={formKey}
                  mode="edit"
                  candidate={r.candidate}
                  readOnly={false}
                  orgId={orgId}
                  userId={userId}
                  formId="role-fit-candidate-form"
                  onSave={(updated) => {
                    onCandidateSaved(updated);
                    setEditing(false);
                    setFormKey((k) => k + 1);
                  }}
                />
                <Link to={`/candidates/${r.candidate.id}`}
                  className="inline-flex items-center gap-1.5 text-sm" style={{ color: BRAND }}>
                  <ExternalLink className="h-4 w-4" /> Open full candidate page
                </Link>
              </div>
            </div>
          )}

          {activeTab === 'overview' && !editing && (
            <>
              {/* Match summary */}
              <div className="ggj-param-group overflow-hidden">
                <div className="ggj-profile-section-h flex items-center gap-2">
                  <Activity className="w-3.5 h-3.5" style={{ color: BRAND }} /> Match Summary
                </div>
                <div className="ggj-profile-section-body">
                  <dl className="ggj-attr-grid">
                    <dt className={DT_CLS}>Fitment score</dt>
                    <dd className={DD_CLS}>
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-sm tabular-nums" style={{ color: band.color }}>{r.fitmentScore}</span>
                        <FitmentStarRating score={r.fitmentScore} />
                        <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full"
                          style={{ background: `${band.color}18`, color: band.color }}>{band.label}</span>
                      </div>
                    </dd>
                    <dt className={DT_CLS}>Coverage</dt>
                    <dd className={DD_CLS}>{r.coverageScore}%</dd>
                    <dt className={DT_CLS}>ATS overall</dt>
                    <dd className={DD_CLS}>
                      {r.atsOverall} <span className="vscode-text-tertiary font-normal">({r.atsRating})</span>
                    </dd>
                    <dt className={DT_CLS}>Missing skills</dt>
                    <dd className={DD_CLS}>
                      <span className="tabular-nums" style={{ color: r.gap.missingSkills.length > 0 ? '#f59e0b' : '#22c55e' }}>
                        {r.gap.missingSkills.length}
                      </span>
                    </dd>
                    <dt className={DT_CLS}>Strong matches</dt>
                    <dd className={DD_CLS}>{r.gap.strongMatches.length}</dd>
                  </dl>
                </div>
              </div>

              {/* Candidate info */}
              <div className="ggj-param-group overflow-hidden">
                <div className="ggj-profile-section-h flex items-center gap-2">
                  <User className="w-3.5 h-3.5" style={{ color: BRAND }} /> Candidate
                </div>
                <div className="ggj-profile-section-body">
                  <dl className="ggj-attr-grid">
                    <dt className={DT_CLS}>Name</dt>
                    <dd className={DD_CLS}>
                      <Link to={`/candidates/${r.candidate.id}`} className="hover:underline" style={{ color: BRAND }}>
                        {r.candidate.full_name}
                      </Link>
                    </dd>
                    <dt className={DT_CLS}>Current role</dt>
                    <dd className={DD_CLS}>
                      {[r.candidate.current_title, r.candidate.current_company].filter(Boolean).join(' @ ') || '—'}
                    </dd>
                    <dt className={DT_CLS}>Email</dt>
                    <dd className={DD_CLS}>{r.candidate.email || '—'}</dd>
                    <dt className={DT_CLS}>Location</dt>
                    <dd className={DD_CLS}>{r.candidate.location || '—'}</dd>
                    <dt className={DT_CLS}>Recruiter rating</dt>
                    <dd className={DD_CLS}>
                      <RecruiterStarsReadOnly rating={r.candidate.recruiter_rating ?? null} />
                    </dd>
                    <dt className={DT_CLS}>Base resume</dt>
                    <dd className={DD_CLS}>
                      {resumeOpenHref ? (
                        <div className="flex flex-wrap items-center gap-3">
                          <button type="button"
                            onClick={() => void openOverviewBaseResumePreview()}
                            disabled={profilePreviewBusy}
                            className="inline-flex items-center gap-2 rounded-lg border px-3 py-1 text-xs font-medium transition-colors disabled:opacity-50"
                            style={{ borderColor: `${BRAND}40`, background: `${BRAND}10`, color: BRAND }}>
                            {profilePreviewBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
                            Preview
                          </button>
                          <a href={resumeOpenHref} target="_blank" rel="noopener noreferrer"
                            className="text-xs hover:underline" style={{ color: BRAND }}>
                            Open in new tab
                          </a>
                        </div>
                      ) : r.candidate.base_resume_url ? (
                        <span className="text-xs vscode-text-tertiary">Preparing link…</span>
                      ) : (
                        <span className="text-xs vscode-text-tertiary">No resume — use Edit to upload.</span>
                      )}
                    </dd>
                  </dl>
                </div>
              </div>

              {/* Role & client */}
              <div className="ggj-param-group overflow-hidden">
                <div className="ggj-profile-section-h flex items-center gap-2">
                  <Briefcase className="w-3.5 h-3.5" style={{ color: BRAND }} /> Role & Client
                </div>
                <div className="ggj-profile-section-body">
                  <dl className="ggj-attr-grid">
                    <dt className={DT_CLS}>Job title</dt>
                    <dd className={DD_CLS}>
                      <Link to={`/jobs/${r.job.id}`} className="hover:underline" style={{ color: BRAND }}>
                        {r.job.title}
                      </Link>
                    </dd>
                    <dt className={DT_CLS}>Client</dt>
                    <dd className={DD_CLS}>
                      <Link to={clientHref} className="hover:underline vscode-text-secondary">
                        {r.job.client?.name ?? '—'}
                      </Link>
                    </dd>
                    <dt className={DT_CLS}>Seniority (JD)</dt>
                    <dd className={DD_CLS}>{r.jd.seniority || '—'}</dd>
                    <dt className={DT_CLS}>Required skills (JD)</dt>
                    <dd className={DD_CLS}>{r.jd.requiredSkills.slice(0, 8).join(', ') || '—'}</dd>
                  </dl>
                </div>
              </div>
            </>
          )}

          {/* ════ EMAILS TAB ════ */}
          {activeTab === 'emails' && (
            <div className="ggj-param-group overflow-hidden">
              <div className="ggj-profile-section-h flex items-center gap-2">
                <Mail className="w-3.5 h-3.5" style={{ color: BRAND }} /> Emails
              </div>
              <div className="ggj-profile-section-body space-y-3">
                <p className="text-sm vscode-text-secondary leading-relaxed">
                  Email threads are tied to an <strong className="vscode-text-primary">application</strong> (candidate × job).
                  This matrix row is a fit preview — no application has been submitted yet.
                </p>
                <p className="text-xs vscode-text-tertiary leading-relaxed">
                  Open the candidate to see their applications, or submit them to this job from the job or candidate workflow to create an application and unlock email threads.
                </p>
                <div className="flex flex-wrap gap-2 pt-1">
                  <Link to={`/candidates/${r.candidate.id}`}
                    className="ggj-toolbar-btn inline-flex items-center gap-1.5 text-xs">
                    <User className="w-3.5 h-3.5" /> Go to Candidate
                  </Link>
                  <Link to={`/jobs/${r.job.id}`}
                    className="ggj-toolbar-btn inline-flex items-center gap-1.5 text-xs">
                    <Briefcase className="w-3.5 h-3.5" /> Go to Job
                  </Link>
                  <Link to="/emails/compose"
                    className="ggj-toolbar-btn inline-flex items-center gap-1.5 text-xs">
                    <Mail className="w-3.5 h-3.5" /> Compose Email
                  </Link>
                </div>
              </div>
            </div>
          )}

          {/* ════ FITMENT TAB ════ */}
          {activeTab === 'fitment' && (
            <div className="space-y-4">

              {/* JD context */}
              <div className="ggj-param-group overflow-hidden">
                <div className="ggj-profile-section-h flex items-center gap-2">
                  <Briefcase className="w-3.5 h-3.5" style={{ color: BRAND }} /> Job Context (Extracted JD)
                </div>
                <div className="ggj-profile-section-body">
                  <dl className="ggj-attr-grid">
                    <dt className={DT_CLS}>Title</dt>
                    <dd className={DD_CLS}>{r.jd.title || '—'}</dd>
                    <dt className={DT_CLS}>Seniority</dt>
                    <dd className={DD_CLS}>{r.jd.seniority || '—'}</dd>
                    <dt className={DT_CLS}>Required skills</dt>
                    <dd className={DD_CLS}>{r.jd.requiredSkills.length ? r.jd.requiredSkills.join(', ') : '—'}</dd>
                    <dt className={DT_CLS}>Tools / domain</dt>
                    <dd className={DD_CLS}>{[...r.jd.tools, ...r.jd.domainKeywords].slice(0, 14).join(', ') || '—'}</dd>
                  </dl>
                </div>
              </div>

              {/* ATS section scores */}
              <div className="ggj-param-group overflow-hidden">
                <div className="ggj-profile-section-h flex items-center gap-2">
                  <TrendingUp className="w-3.5 h-3.5" style={{ color: BRAND }} /> ATS Section Scores
                </div>
                <div className="ggj-profile-section-body">
                  <dl className="ggj-attr-grid">
                    {[
                      ['Formatting', r.atsSections.formatting],
                      ['Keywords',   r.atsSections.keywords  ],
                      ['Structure',  r.atsSections.structure ],
                      ['Content',    r.atsSections.content   ],
                    ].map(([k, v]) => (
                      <>
                        <dt key={`k-${k}`} className={DT_CLS}>{k as string}</dt>
                        <dd key={`v-${k}`} className={DD_CLS}>
                          <span className="tabular-nums font-mono">{String(v)}</span>
                        </dd>
                      </>
                    ))}
                  </dl>
                </div>
              </div>

              {/* Saved resume */}
              {(savedResumePreview.status === 'loading' || savedResumePreview.status !== 'idle' || r.candidate.base_resume_url) && (
                <div className="ggj-param-group overflow-hidden">
                  <div className="ggj-profile-section-h flex items-center gap-2">
                    <ExternalLink className="w-3.5 h-3.5" style={{ color: BRAND }} /> Optimized Resume (Saved to Profile)
                  </div>
                  <div className="ggj-profile-section-body">
                    {savedResumePreview.status === 'loading' && (
                      <p className="text-xs vscode-text-tertiary">Loading…</p>
                    )}
                    {(savedResumePreview.status === 'pdf' || savedResumePreview.status === 'text') && (
                      <div className="flex flex-wrap items-center gap-3">
                        <button type="button"
                          onClick={() =>
                            setResumePreviewModal({
                              payload: savedResumePreview.status === 'pdf'
                                ? { kind: 'pdf', url: savedResumePreview.openHref }
                                : { kind: 'text', body: (savedResumePreview as { body: string }).body, templateId: (savedResumePreview as { templateId: string }).templateId, design: (savedResumePreview as { design?: SavedProfileDesign }).design },
                              headline: optimizedResumeModalHeadline || r.candidate.full_name || 'Saved resume',
                              eyebrow: 'Optimized resume (saved to profile)',
                            })
                          }
                          className="inline-flex items-center rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors"
                          style={{ borderColor: `${BRAND}40`, background: `${BRAND}10`, color: BRAND }}>
                          Preview
                        </button>
                        <a href={savedResumePreview.openHref} target="_blank" rel="noopener noreferrer"
                          className="text-xs hover:underline" style={{ color: BRAND }}>
                          Open in new tab
                        </a>
                      </div>
                    )}
                    {savedResumePreview.status === 'external' && (
                      <a href={savedResumePreview.href} target="_blank" rel="noopener noreferrer"
                        className="text-sm hover:underline" style={{ color: BRAND }}>
                        {savedResumePreview.label}
                      </a>
                    )}
                    {savedResumePreview.status === 'idle' && !r.candidate.base_resume_url && (
                      <p className="text-xs vscode-text-tertiary">
                        After you <strong className="vscode-text-secondary">Run Optimize</strong> and save to the candidate profile, the formatted resume appears here.
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Skill gap analysis */}
              <div className="ggj-param-group overflow-hidden">
                <div className="ggj-profile-section-h flex items-center gap-2">
                  <Activity className="w-3.5 h-3.5" style={{ color: BRAND }} /> Skill Gap Analysis
                </div>
                <div className="ggj-profile-section-body">
                  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    <GapList title="Strong matches" items={r.gap.strongMatches.map((x) => x.skill)} tone="text-green-400/90" />
                    <GapList title="Weak / list-only" items={r.gap.weakMatches.map((x) => x.skill)} tone="text-yellow-300/90" />
                    <GapList title="Implied" items={r.gap.impliedMatches.map((x) => x.skill)} tone="text-sky-300/90" />
                    <GapList title="Missing" items={r.gap.missingSkills.map((x) => x.skill)} tone="text-red-300/90" />
                  </div>
                </div>
              </div>

              {/* Weak bullets */}
              {r.gap.weakBullets.length > 0 && (
                <div className="ggj-param-group overflow-hidden">
                  <div className="ggj-profile-section-h">Weak Bullets</div>
                  <div className="ggj-profile-section-body space-y-2">
                    {r.gap.weakBullets.slice(0, 6).map((wb, i) => (
                      <div key={i} className="rounded-lg border vscode-border px-3 py-2.5">
                        <p className="text-xs vscode-text-primary">{wb.text}</p>
                        <p className="mt-1 text-[11px] vscode-text-tertiary">{wb.reason}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ════ PROJECTION ANALYSIS TAB ════ */}
          {activeTab === 'projection-analysis' && (
            <div className="space-y-4">
              {projectionLoading ? (
                <div className="ggj-param-group overflow-hidden">
                  <div className="ggj-profile-section-body">
                    <p className="text-sm vscode-text-tertiary">Loading saved projection…</p>
                  </div>
                </div>
              ) : !savedProjection ? (
                <div className="ggj-param-group overflow-hidden">
                  <div className="ggj-profile-section-h flex items-center gap-2">
                    <BarChart3 className="w-3.5 h-3.5" style={{ color: BRAND }} /> No Saved Projection
                  </div>
                  <div className="ggj-profile-section-body space-y-3">
                    <p className="text-sm vscode-text-secondary leading-relaxed">
                      No saved projection for this pair yet. Create one from <strong className="vscode-text-primary">RoleFit AI</strong> using{' '}
                      <strong className="vscode-text-primary">New Projection</strong>, or run <strong className="vscode-text-primary">Full AI Optimize</strong>{' '}
                      from the 1:1 flow — results appear here after save.
                    </p>
                    <Link to="/role-fit-ai/1-1" className="inline-flex items-center gap-1.5 text-xs font-medium" style={{ color: BRAND }}>
                      <ArrowRight className="w-3.5 h-3.5" /> Open RoleFit AI
                    </Link>
                  </div>
                </div>
              ) : (
                <>
                  <div className="ggj-param-group overflow-hidden">
                    <div className="ggj-profile-section-h flex items-center gap-2">
                      <BarChart3 className="w-3.5 h-3.5" style={{ color: BRAND }} /> Projection Summary
                    </div>
                    <div className="ggj-profile-section-body">
                      <dl className="ggj-attr-grid">
                        {[
                          ['Topology',         savedProjection.topology],
                          ['Fitment score',    String(savedProjection.fitment_score)],
                          ['ATS overall',      `${savedProjection.ats_overall} (${savedProjection.ats_rating})`],
                          ['Projected range',  savedProjection.projected_match_min != null ? `${savedProjection.projected_match_min}–${savedProjection.projected_match_max}%` : '—'],
                          ['Last updated',     formatDateTimeGmt(savedProjection.updated_at)],
                        ].map(([k, v]) => (
                          <>
                            <dt key={`k-${k}`} className={DT_CLS}>{k}</dt>
                            <dd key={`v-${k}`} className={DD_CLS}>{v}</dd>
                          </>
                        ))}
                      </dl>
                    </div>
                  </div>

                  {(() => {
                    const aj = savedProjection.analysis_json ?? {};
                    const matched = skillsFromAnalysisJson(aj['matchedSkills']);
                    const missing = skillsFromAnalysisJson(aj['missingSkills']);
                    const implied = skillsFromAnalysisJson(aj['impliedSkills']);
                    const atsSec = aj['atsSections'] as Record<string, number> | undefined;
                    const source = aj['source'] as string | undefined;
                    return (
                      <div className="space-y-4">
                        {source && (
                          <div className="ggj-param-group overflow-hidden">
                            <div className="ggj-profile-section-h">Analysis Source</div>
                            <div className="ggj-profile-section-body">
                              <dl className="ggj-attr-grid">
                                <dt className={DT_CLS}>Source</dt>
                                <dd className={`${DD_CLS} font-mono`}>{source}</dd>
                                {aj['optimized_at'] && (
                                  <>
                                    <dt className={DT_CLS}>Optimized at</dt>
                                    <dd className={DD_CLS}>{String(aj['optimized_at'])}</dd>
                                  </>
                                )}
                              </dl>
                            </div>
                          </div>
                        )}

                        {(matched.length > 0 || missing.length > 0 || implied.length > 0) && (
                          <div className="ggj-param-group overflow-hidden">
                            <div className="ggj-profile-section-h">Skill Analysis (Saved)</div>
                            <div className="ggj-profile-section-body space-y-3">
                              {matched.length > 0 && (
                                <div>
                                  <p className="text-[10px] uppercase tracking-wide vscode-text-tertiary mb-1">Matched skills</p>
                                  <p className="text-xs vscode-text-secondary leading-relaxed">{matched.slice(0, 80).join(' · ')}</p>
                                </div>
                              )}
                              {missing.length > 0 && (
                                <div>
                                  <p className="text-[10px] uppercase tracking-wide vscode-text-tertiary mb-1">Missing skills</p>
                                  <p className="text-xs leading-relaxed text-amber-400/80">{missing.slice(0, 80).join(' · ')}</p>
                                </div>
                              )}
                              {implied.length > 0 && (
                                <div>
                                  <p className="text-[10px] uppercase tracking-wide vscode-text-tertiary mb-1">Implied skills</p>
                                  <p className="text-xs leading-relaxed text-sky-400/80">{implied.slice(0, 40).join(' · ')}</p>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {atsSec && typeof atsSec === 'object' && (
                          <div className="ggj-param-group overflow-hidden">
                            <div className="ggj-profile-section-h">ATS Section Scores (Saved)</div>
                            <div className="ggj-profile-section-body">
                              <dl className="ggj-attr-grid">
                                {typeof atsSec.formatting === 'number' && (<><dt className={DT_CLS}>Formatting</dt><dd className={DD_CLS}>{atsSec.formatting}</dd></>)}
                                {typeof atsSec.keywords   === 'number' && (<><dt className={DT_CLS}>Keywords</dt><dd className={DD_CLS}>{atsSec.keywords}</dd></>)}
                                {typeof atsSec.structure  === 'number' && (<><dt className={DT_CLS}>Structure</dt><dd className={DD_CLS}>{atsSec.structure}</dd></>)}
                                {typeof atsSec.content    === 'number' && (<><dt className={DT_CLS}>Content</dt><dd className={DD_CLS}>{atsSec.content}</dd></>)}
                              </dl>
                            </div>
                          </div>
                        )}

                        <div className="ggj-param-group overflow-hidden">
                          <details>
                            <summary className="ggj-profile-section-h cursor-pointer hover:vscode-bg-hover">
                              Raw Analysis JSON
                            </summary>
                            <div className="ggj-profile-section-body">
                              <pre className="max-h-64 overflow-auto text-[11px] vscode-text-tertiary whitespace-pre-wrap break-all font-mono">
                                {JSON.stringify(savedProjection.analysis_json, null, 2)}
                              </pre>
                            </div>
                          </details>
                        </div>
                      </div>
                    );
                  })()}
                </>
              )}
            </div>
          )}

          {/* ════ PARAMETERS TAB ════ */}
          {activeTab === 'parameters' && (
            <div className="space-y-4">

              <div className="ggj-param-group overflow-hidden">
                <div className="ggj-param-section-block border-b border-[color-mix(in_srgb,var(--vscode-border-primary)_55%,transparent)]">
                  <div className="ggj-profile-section-h">Object</div>
                  <div className="ggj-profile-section-body">
                    <dl className="ggj-attr-grid">
                      <dt className={DT_CLS}>Name</dt>
                      <dd className={`${DD_CLS} font-medium`}>{r.candidate.full_name} — {r.job.title}</dd>
                      <dt className={DT_CLS}>Object type</dt>
                      <dd className={`${DD_CLS} vscode-text-secondary`}>Role-fit pair (read-only)</dd>
                      <dt className={DT_CLS}>Description</dt>
                      <dd className={`${DD_CLS} min-w-0 lg:col-span-3`}>
                        <textarea
                          readOnly
                          tabIndex={-1}
                          rows={3}
                          value=""
                          placeholder="—"
                          aria-label="Description"
                          className="ggj-param-readonly-textarea"
                        />
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>

              <div className="ggj-param-group overflow-hidden">
                <div className="ggj-profile-section-h flex items-center gap-2">
                  <Hash className="w-3.5 h-3.5" style={{ color: BRAND }} /> Pair Identifiers
                </div>
                <div className="ggj-profile-section-body">
                  <dl className="ggj-attr-grid">
                    {[
                      ['Pair key',     r.key          ],
                      ['Candidate ID', r.candidate.id ],
                      ['Job ID',       r.job.id       ],
                      ['Client ID',    r.job.client_id],
                    ].map(([k, v]) => (
                      <>
                        <dt key={`k-${k}`} className={DT_CLS}>{k}</dt>
                        <dd key={`v-${k}`} className={`${DD_CLS} font-mono break-all`}>{v}</dd>
                      </>
                    ))}
                  </dl>
                </div>
              </div>

              <div className="ggj-param-group overflow-hidden">
                <div className="ggj-profile-section-h flex items-center gap-2">
                  <Activity className="w-3.5 h-3.5" style={{ color: BRAND }} /> Compute Parameters
                </div>
                <div className="ggj-profile-section-body">
                  <dl className="ggj-attr-grid">
                    {[
                      ['Algorithm',    'computeRoleFitPair'                       ],
                      ['Fitment score',String(r.fitmentScore)                     ],
                      ['Coverage',     `${r.coverageScore}%`                      ],
                      ['ATS overall',  String(r.atsOverall)                       ],
                      ['ATS rating',   r.atsRating                                ],
                      ['Strong matches', String(r.gap.strongMatches.length)       ],
                      ['Weak matches',   String(r.gap.weakMatches.length)         ],
                      ['Implied matches',String(r.gap.impliedMatches.length)      ],
                      ['Missing skills', String(r.gap.missingSkills.length)       ],
                    ].map(([k, v]) => (
                      <>
                        <dt key={`k-${k}`} className={DT_CLS}>{k}</dt>
                        <dd key={`v-${k}`} className={DD_CLS}>{v}</dd>
                      </>
                    ))}
                  </dl>
                </div>
              </div>

              <div className="ggj-param-group overflow-hidden">
                <div className="ggj-profile-section-h flex items-center gap-2">
                  <Building2 className="w-3.5 h-3.5" style={{ color: BRAND }} /> Org Context
                </div>
                <div className="ggj-profile-section-body">
                  <dl className="ggj-attr-grid">
                    {[
                      ['Org ID',    orgId  ],
                      ['User ID',   userId ],
                    ].map(([k, v]) => (
                      <>
                        <dt key={`k-${k}`} className={DT_CLS}>{k}</dt>
                        <dd key={`v-${k}`} className={`${DD_CLS} font-mono break-all`}>{v}</dd>
                      </>
                    ))}
                  </dl>
                </div>
              </div>

            </div>
          )}
        </div>

        {/* ── RIGHT: 340px sticky sidebar — hidden on Parameters tab ── */}
        {activeTab !== 'parameters' && (
        <aside className="ggj-rail-aside space-y-3 lg:sticky lg:top-4 self-start">

          {/* Fitment at a glance */}
          <CandidateCollapsiblePanel storageKey="rolefit-pair-fitment" title="Fitment at a Glance" defaultOpen>
            <div className="space-y-1.5 px-1">
              {[
                ['Fitment',   r.fitmentScore,   band.color   ],
                ['Coverage',  `${r.coverageScore}%`, '#3b82f6'],
                ['ATS',       r.atsOverall,     '#8B80F9'    ],
              ].map(([k, v, c]) => (
                <div key={String(k)} className="flex items-center justify-between py-1 border-b border-[var(--vscode-border-primary)]/30 last:border-0">
                  <span className="text-xs vscode-text-tertiary">{String(k)}</span>
                  <span className="text-sm font-bold" style={{ color: String(c) }}>{String(v)}</span>
                </div>
              ))}
              <div className="pt-1">
                <FitmentStarRating score={r.fitmentScore} />
              </div>
            </div>
          </CandidateCollapsiblePanel>

          {/* Candidate summary */}
          <CandidateCollapsiblePanel storageKey="rolefit-pair-candidate" title="Candidate" defaultOpen>
            <div className="space-y-1.5 px-1">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold shrink-0"
                  style={{ background: `${BRAND}20`, color: BRAND }}>
                  {r.candidate.full_name[0]?.toUpperCase()}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold vscode-text-primary truncate">{r.candidate.full_name}</p>
                  {r.candidate.email && <p className="text-[10px] vscode-text-tertiary truncate">{r.candidate.email}</p>}
                </div>
              </div>
              {[
                ['Role', [r.candidate.current_title, r.candidate.current_company].filter(Boolean).join(' @ ') || '—'],
                ['Location', r.candidate.location || '—'],
              ].map(([k, v]) => (
                <div key={String(k)} className="flex items-center justify-between py-1 border-b border-[var(--vscode-border-primary)]/30 last:border-0">
                  <span className="text-xs vscode-text-tertiary">{String(k)}</span>
                  <span className="text-xs vscode-text-primary font-medium text-right max-w-[160px] truncate">{String(v)}</span>
                </div>
              ))}
              <Link to={`/candidates/${r.candidate.id}`}
                className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium" style={{ color: BRAND }}>
                <ExternalLink className="w-3 h-3" /> Open profile
              </Link>
            </div>
          </CandidateCollapsiblePanel>

          {/* Job & Client */}
          <CandidateCollapsiblePanel storageKey="rolefit-pair-job" title="Job & Client" defaultOpen>
            <div className="space-y-1.5 px-1">
              {[
                ['Job',    r.job.title             ],
                ['Client', r.job.client?.name ?? '—'],
              ].map(([k, v]) => (
                <div key={String(k)} className="flex items-start justify-between gap-2 py-1 border-b border-[var(--vscode-border-primary)]/30 last:border-0">
                  <span className="text-xs vscode-text-tertiary shrink-0">{String(k)}</span>
                  <span className="text-xs vscode-text-primary font-medium text-right">{String(v)}</span>
                </div>
              ))}
              <div className="flex gap-2 pt-1">
                <Link to={`/jobs/${r.job.id}`}
                  className="inline-flex items-center gap-1.5 text-xs font-medium" style={{ color: BRAND }}>
                  <ExternalLink className="w-3 h-3" /> Job page
                </Link>
                <Link to={clientHref}
                  className="inline-flex items-center gap-1.5 text-xs vscode-text-secondary hover:vscode-text-primary">
                  <Building2 className="w-3 h-3" /> Client page
                </Link>
              </div>
            </div>
          </CandidateCollapsiblePanel>

          {/* AI Actions */}
          <CandidateCollapsiblePanel storageKey="rolefit-pair-actions" title="AI Actions" defaultOpen>
            <div className="space-y-2 px-1">
              <button type="button" onClick={runOptimize}
                className="w-full inline-flex items-center justify-center gap-2 rounded-lg py-2 text-sm font-semibold transition-colors"
                style={{ background: `${BRAND}15`, color: BRAND }}>
                <Zap className="w-4 h-4" /> Run Optimize
              </button>
              <p className="text-[10px] vscode-text-tertiary leading-relaxed text-center">
                AI resume optimization for this candidate × job pair. Returns here after completion.
              </p>
            </div>
          </CandidateCollapsiblePanel>

          {/* Quick links */}
          <CandidateCollapsiblePanel storageKey="rolefit-pair-links" title="Quick Links" defaultOpen>
            <div className="-mx-4 -mb-3">
              {[
                { label: r.candidate.full_name,         to: `/candidates/${r.candidate.id}` },
                { label: r.job.title,                   to: `/jobs/${r.job.id}`             },
                { label: 'RoleFit AI',                  to: '/role-fit-ai/1-1'              },
                { label: 'Role Fit Matrix',             to: '/role-fit'                     },
                { label: 'Single Optimize',             to: '/single-optimize'              },
              ].map(({ label, to }) => (
                <Link key={to} to={to}
                  className="flex items-center gap-2.5 px-4 py-2.5 text-sm vscode-text-secondary hover:vscode-text-primary hover:vscode-bg-hover transition-colors border-b border-[var(--vscode-border-primary)]/30 last:border-0">
                  <ArrowRight className="w-3.5 h-3.5 shrink-0 vscode-text-tertiary" /> {label}
                </Link>
              ))}
            </div>
          </CandidateCollapsiblePanel>

        </aside>
        )}
      </div>

      <OptimizedResumePreviewModal
        open={resumePreviewModal !== null}
        onClose={() => setResumePreviewModal(null)}
        headline={resumePreviewModal?.headline ?? ''}
        payload={resumePreviewModal?.payload ?? null}
        eyebrow={resumePreviewModal?.eyebrow}
        consultancyWatermark={user?.orgName ?? null}
        downloadFileBase={r.candidate.full_name}
      />
    </div>
  );
}
